import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
} from "@mui/material";

const INITIAL_FORM = {
  expected_discharge_date: "",
  expected_discharge_time: "",
  notes: "",
};

// Doctor's "Discharge Advice" care-plan modal (Task C20-2).
// Flows through the panel's handleAddCarePlan (real care_plans/ POST) —
// no direct API call here. Backend's handle_discharge_advice(data) reads
// expected_discharge_date + expected_discharge_time; notes is optional.
const DischargeAdviceModal = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);

  useEffect(() => {
    if (open) setFormData(INITIAL_FORM);
  }, [open]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const canSubmit =
    formData.expected_discharge_date.trim() !== "" &&
    formData.expected_discharge_time.trim() !== "";

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(
      {
        expected_discharge_date: formData.expected_discharge_date,
        expected_discharge_time: formData.expected_discharge_time,
        notes: formData.notes,
      },
      {},
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: "#121959", pb: 1 }}>
        Discharge Advice
      </DialogTitle>
      <Divider />
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: "0.85rem", color: "#374151", mb: 0.5 }}>
                Expected Discharge Date
                <Typography component="span" sx={{ color: "error.main", ml: 0.3 }}>
                  *
                </Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                required
                value={formData.expected_discharge_date}
                onChange={handleChange("expected_discharge_date")}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: "0.85rem", color: "#374151", mb: 0.5 }}>
                Expected Discharge Time
                <Typography component="span" sx={{ color: "error.main", ml: 0.3 }}>
                  *
                </Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="time"
                required
                value={formData.expected_discharge_time}
                onChange={handleChange("expected_discharge_time")}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontWeight: 500, fontSize: "0.85rem", color: "#374151", mb: 0.5 }}>
                Notes{" "}
                <Typography component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", ml: 0.5 }}>
                  (Optional)
                </Typography>
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                placeholder="Additional discharge advice notes"
                value={formData.notes}
                onChange={handleChange("notes")}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} variant="outlined" size="small">
            CANCEL
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={!canSubmit}
            sx={{ backgroundColor: "#0E3F66", "&:hover": { backgroundColor: "#0a2e4d" } }}
          >
            SAVE DISCHARGE ADVICE
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DischargeAdviceModal;
